import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSubscriptionOffers, createSubscriptionOffer, updateSubscriptionOffer, deleteSubscriptionOffer, getCustomers, getProspects } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Eye, Download, Calendar, User, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PageHeader from '@/components/PageHeader';
import { getLocalDateString } from '@/utils/dateUtils';

// Sabit ürün listesi
const SUBSCRIPTION_PRODUCTS = [
  "Lisans Bedeli V18.01",
  "Turkcell M2M",
  "Vodafone M2M",
  "Türk Telekom M2M"
];

const SubscriptionOffers = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [offers, setOffers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Tarih filtreleri
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(format(firstDayOfMonth, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(today, 'yyyy-MM-dd'));
  const [filteredOffers, setFilteredOffers] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    title: 'Arvento Abonelik Fiyat Teklifi',
    duration_months: 12,
    items: [{ product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Müşteri veya müşteri adayı detay sayfasından gelindiyse otomatik formu aç
    if (location.state?.customerId && (customers.length > 0 || prospects.length > 0)) {
      const customer = customers.find(c => c.id === location.state.customerId) ||
                      prospects.find(p => p.id === location.state.customerId);
      
      setFormData({
        ...formData,
        customer_id: location.state.customerId
      });
      if (customer) {
        setCustomerSearch(customer.name);
        setShowCustomerDropdown(false);
      }
      setShowDialog(true);
      navigate(location.pathname, { replace: true });
    }
  }, [customers, prospects, location.state]);

  // Dışarı tıklandığında dropdown'u kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [offersData, customersData, prospectsData] = await Promise.all([
        getSubscriptionOffers(),
        getCustomers(),
        getProspects()
      ]);
      setOffers(offersData);
      setCustomers(customersData);
      setProspects(prospectsData);
      filterOffers(offersData);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filterOffers = (offersToFilter = offers) => {
    let filtered = offersToFilter;
    
    if (startDate && endDate) {
      filtered = filtered.filter(offer => {
        const offerDate = getLocalDateString(new Date(offer.created_at));
        return offerDate >= startDate && offerDate <= endDate;
      });
    }
    
    setFilteredOffers(filtered);
  };
  
  useEffect(() => {
    filterOffers();
  }, [startDate, endDate, offers]);

  // İndirimli fiyat hesapla
  const calculateDiscountedPrice = (item) => {
    const annualPrice = parseFloat(item.annual_price) || 0;
    const discountPercentage = parseFloat(item.discount_percentage) || 0;
    
    if (discountPercentage > 0) {
      return annualPrice * (1 - discountPercentage / 100);
    }
    return annualPrice;
  };

  // Ürün için toplam hesapla
  const calculateItemTotal = (item) => {
    const discountedPrice = calculateDiscountedPrice(item);
    const quantity = parseInt(item.quantity) || 1;
    const yearMultiplier = parseInt(formData.duration_months) / 12; // 12->1, 24->2, 36->3
    
    return discountedPrice * quantity * yearMultiplier;
  };

  // Toplam hesaplama
  const calculateTotals = () => {
    let subtotal = 0;
    formData.items.forEach(item => {
      subtotal += calculateItemTotal(item);
    });
    const vatAmount = subtotal * 0.20;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  // Ürün ekle
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }]
    });
  };

  // Ürün sil
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Ürün güncelle
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Lütfen müşteri seçin');
      return;
    }

    // Ürün kontrolü
    const validItems = formData.items.filter(item => item.product_name && item.annual_price);
    if (validItems.length === 0) {
      toast.error('Lütfen en az bir ürün ekleyin');
      return;
    }

    try {
      const offerData = {
        customer_id: formData.customer_id,
        title: formData.title,
        duration_months: parseInt(formData.duration_months),
        items: validItems.map(item => ({
          product_name: item.product_name,
          quantity: parseInt(item.quantity) || 1,
          annual_price: parseFloat(item.annual_price),
          discount_percentage: parseFloat(item.discount_percentage) || 0
        })),
        notes: formData.notes
      };
      
      if (editingOffer) {
        await updateSubscriptionOffer(editingOffer.id, offerData);
        toast.success('Teklif başarıyla güncellendi');
      } else {
        await createSubscriptionOffer(offerData);
        toast.success('Teklif başarıyla oluşturuldu');
      }
      
      setShowDialog(false);
      setEditingOffer(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Teklif kaydedilirken hata oluştu');
      console.error('Error saving offer:', error);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    
    // items array'ini kontrol et (eski yapı için uyumluluk)
    let items = [];
    if (offer.items && Array.isArray(offer.items)) {
      items = offer.items.map(item => ({
        product_name: item.product_name || '',
        quantity: item.quantity || 1,
        annual_price: item.annual_price || item.price || '',
        discount_percentage: item.discount_percentage || 0
      }));
    } else if (offer.item) {
      // Eski tek ürün yapısı
      items = [{
        product_name: offer.item.product_name || offer.title || '',
        quantity: offer.item.quantity || 1,
        annual_price: offer.item.annual_price || offer.item.price || '',
        discount_percentage: offer.item.discount_percentage || 0
      }];
    }
    
    if (items.length === 0) {
      items = [{ product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }];
    }
    
    setFormData({
      customer_id: offer.customer_id,
      title: offer.title || 'Abonelik Fiyat Teklifi',
      duration_months: offer.duration_months,
      items: items,
      notes: offer.notes || ''
    });
    setCustomerSearch(offer.customer_name);
    setShowCustomerDropdown(false);
    setShowDialog(true);
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Bu teklifi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteSubscriptionOffer(offerId);
      toast.success('Teklif başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error('Teklif silinirken hata oluştu');
      console.error('Error deleting offer:', error);
    }
  };

  const handlePreview = (offer) => {
    setPreviewOffer(offer);
    setShowPreview(true);
  };

  const handleDownloadPDF = async (offer) => {
    try {
      const customerName = offer.customer_name.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, '').trim();
      const filename = `ArventoAbonelik-${customerName}-${offer.offer_number}.pdf`;
      
      const htmlContent = generateOfferHTML(offer);
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '794px';
      tempDiv.style.height = '1123px';
      tempDiv.style.background = 'white';
      tempDiv.style.overflow = 'hidden';
      document.body.appendChild(tempDiv);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const canvas = await html2canvas(tempDiv, {
        scale: 1.2,
        useCORS: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123,
        imageTimeout: 0,
        removeContainer: true
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      pdf.save(filename);
      
      document.body.removeChild(tempDiv);
      
      // Preview açıksa kapat
      if (showPreview) {
        setShowPreview(false);
      }
      toast.success('PDF başarıyla indirildi');
      
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      toast.error('PDF oluşturulurken hata oluştu: ' + err.message);
    }
  };

  const generateOfferHTML = (offer) => {
    // items array'ini kontrol et (eski yapı için uyumluluk)
    let items = [];
    if (offer.items && Array.isArray(offer.items)) {
      items = offer.items;
    } else if (offer.item) {
      items = [offer.item];
    }
    
    const subtotal = offer.subtotal || items.reduce((sum, item) => sum + (item.total || 0), 0);
    const vatAmount = offer.vat_amount || subtotal * 0.20;
    const totalAmount = offer.total_amount || subtotal + vatAmount;
    
    // Tablo satırları - Sadece İndirimli Fiyat gösterilecek
    const itemsHtml = items.map(item => {
      const annualPrice = item.annual_price || item.price || 0;
      const discountedPrice = item.discounted_price || annualPrice;
      
      return `
        <tr>
          <td style="font-weight: 500;">${item.product_name}</td>
          <td style="text-align: center;">${item.quantity || 1}</td>
          <td style="text-align: right; color: #2563EB; font-weight: 600;">${discountedPrice.toFixed(2)} ₺</td>
          <td style="text-align: center;">${offer.duration_months} Ay</td>
          <td style="text-align: right; font-weight: 700; color: #0f172a;">${(item.total || 0).toFixed(2)} ₺</td>
        </tr>
      `;
    }).join('');
    
    return `
      <!DOCTYPE html>
      <html lang="tr">
      <head>
        <meta charset="utf-8">
        <title>Abonelik Teklifi - ${offer.offer_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box; 
          }
          
          body { 
            font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
            margin: 0;
            padding: 0;
            background: white;
            -webkit-font-smoothing: antialiased;
          }
          
          .container {
            width: 794px;
            height: calc(1123px - 20mm);
            margin: 0 auto;
            padding: 0;
            background: white;
            position: relative;
            page-break-after: always;
          }
          
          @page {
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          
          .header {
            background: #2563EB;
            padding: 30px 40px;
            color: white;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 10mm 10mm 0 10mm;
          }
          
          .header-logo { 
            width: 180px;
            height: auto;
          }
          
          .company-info { 
            font-size: 11px; 
            color: white;
            line-height: 1.8;
            font-weight: 400;
            text-align: right;
            opacity: 0.95;
          }
          .content {
            padding: 20px 0 50px 0;
            margin: 0 10mm;
          }
          
          .offer-title {
            font-size: 28px;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
          }
          
          .offer-subtitle {
            font-size: 14px;
            color: #64748b;
            font-weight: 500;
            margin-bottom: 30px;
          }
          
          table { 
            width: 100%;
            border-collapse: collapse;
            margin: 30px 0;
          }
          
          th { 
            background: white;
            color: #475569; 
            padding: 12px 0; 
            text-align: left;
            font-size: 12px;
            letter-spacing: 0.5px;
            font-weight: 600;
            border-bottom: 1px solid #e2e8f0;
            text-transform: uppercase;
          }
          
          th:not(:first-child), td:not(:first-child) {
            padding-left: 12px;
          }
          
          td { 
            padding: 10px 0; 
            border-bottom: 1px solid #f1f5f9;
            background: white;
            font-size: 13px;
            color: #334155;
          }
          
          .summary-box {
            background: white;
            padding: 0;
            margin: 20px 0;
            border-top: 1px solid #e2e8f0;
          }
          
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 14px;
            color: #475569;
            border-bottom: 1px solid #f1f5f9;
          }
          
          .summary-row:last-child { 
            border-bottom: 2px solid #e2e8f0;
          }
          
          .total-row { 
            font-size: 18px !important;
            font-weight: 700 !important;
            color: #1e293b !important;
          }
          
          .footer { 
            position: absolute;
            bottom: 0;
            left: 10mm;
            right: 10mm;
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            padding: 18px 40px;
            font-size: 13px;
            color: white;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10mm;
          }
          
          .footer-logo {
            height: 80px;
            width: auto;
          }
          
          .duration-badge {
            display: inline-block;
            background: #dbeafe;
            color: #1d4ed8;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
            margin-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <img src="/progo-logo-white.png" alt="ProGo Mobil Sistemler" class="header-logo">
            <div class="company-info">
              Kazımdirik mah. 364/10 Sok. No:11 D:73<br>
              Bornova / İZMİR<br>
              0 232 435 20 02 · info@progofilo.com.tr
            </div>
          </div>
          
          <!-- Content -->
          <div class="content">
            <!-- Title -->
            <div class="offer-title">${offer.title || 'Arvento Abonelik Fiyat Teklifi'}</div>
            <div class="offer-subtitle">Teklif No: ${offer.offer_number}</div>
            
            <!-- Info Grid -->
            <div style="margin: 30px 0;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                <div>
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Müşteri</div>
                  <div style="font-size: 16px; color: #1e293b; font-weight: 600;">${offer.customer_name}</div>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Tarih</div>
                  <div style="font-size: 16px; color: #1e293b; font-weight: 600;">${offer.created_at ? format(new Date(offer.created_at), 'dd.MM.yyyy') : '-'}</div>
                </div>
              </div>
            </div>
            
            <!-- Products Table -->
            <table>
              <thead>
                <tr>
                  <th>Ürün/Hizmet</th>
                  <th style="text-align: center;">Adet</th>
                  <th style="text-align: right;">İndirimli Yıllık Fiyat</th>
                  <th style="text-align: center;">Süre</th>
                  <th style="text-align: right;">Toplam</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>
            
            <!-- Summary -->
            <div class="summary-box">
              <div class="summary-row">
                <span>Ara Toplam</span>
                <span>${subtotal.toFixed(2)} ₺</span>
              </div>
              <div class="summary-row">
                <span>KDV (%20)</span>
                <span>${vatAmount.toFixed(2)} ₺</span>
              </div>
              <div class="summary-row total-row">
                <span>Genel Toplam</span>
                <span>${totalAmount.toFixed(2)} ₺</span>
              </div>
            </div>
            
            <!-- Notes -->
            ${offer.notes ? `
            <div style="margin-top: 30px; padding: 20px; background: #f8fafc; border-radius: 12px; border-left: 4px solid #2563EB;">
              <div style="font-size: 12px; color: #64748b; font-weight: 600; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Notlar</div>
              <div style="font-size: 13px; color: #334155; line-height: 1.6;">${offer.notes}</div>
            </div>
            ` : ''}
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div>
              <div style="font-weight: 600; margin-bottom: 4px;">Yetkili: ${offer.representative_full_name || offer.representative}</div>
              <div style="opacity: 0.9;">${offer.representative_phone || ''}</div>
            </div>
            <img src="/arvento-logo-white.png" alt="Arvento" class="footer-logo">
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      title: 'Arvento Abonelik Fiyat Teklifi',
      duration_months: 12,
      items: [{ product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }],
      notes: ''
    });
    setCustomerSearch('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Abonelik Fiyat Teklifleri" 
        description="Abonelik yenileme tekliflerini oluşturun ve yönetin"
        icon={FileText}
        iconColor="text-purple-600"
        iconBgColor="bg-purple-100"
      />

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Date Filters */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Quick Filter Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                  setStartDate(format(firstDay, 'yyyy-MM-dd'));
                  setEndDate(format(now, 'yyyy-MM-dd'));
                }}
                className={`${
                  startDate === format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd') 
                    ? 'bg-purple-100 border-purple-300 text-purple-700' 
                    : ''
                }`}
              >
                Bu Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                  const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                  setStartDate(format(firstDayLastMonth, 'yyyy-MM-dd'));
                  setEndDate(format(lastDayLastMonth, 'yyyy-MM-dd'));
                }}
              >
                Geçen Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
                  setStartDate(format(threeMonthsAgo, 'yyyy-MM-dd'));
                  setEndDate(format(now, 'yyyy-MM-dd'));
                }}
              >
                Son 3 Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
                  setStartDate(format(sixMonthsAgo, 'yyyy-MM-dd'));
                  setEndDate(format(now, 'yyyy-MM-dd'));
                }}
              >
                Son 6 Ay
              </Button>
            </div>
            
            {/* Date Inputs */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
              <span className="text-gray-500">-</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
          </div>

          {/* New Offer Button */}
          <Button onClick={() => setShowDialog(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Abonelik Teklifi
          </Button>
        </div>
      </div>

      {/* Offers List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Teklif No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Müşteri</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ürün Sayısı</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Süre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tutar (KDV Dahil)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Temsilci</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Henüz abonelik teklifi bulunmuyor</p>
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => {
                  const itemCount = offer.items?.length || (offer.item ? 1 : 0);
                  return (
                    <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-semibold text-purple-600">
                          {offer.offer_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{offer.customer_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {itemCount} Ürün
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                          {offer.duration_months} Ay
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-green-600">
                          {offer.total_amount?.toFixed(2)} ₺
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {offer.created_at ? format(new Date(offer.created_at), 'dd.MM.yyyy', { locale: tr }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {offer.representative_full_name || offer.representative}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(offer)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Önizle"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(offer)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="PDF İndir"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(offer)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            title="Düzenle"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(offer.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Summary Footer */}
        {filteredOffers.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Toplam {filteredOffers.length} teklif
            </span>
            <span className="font-semibold text-green-600">
              Toplam: {filteredOffers.reduce((sum, o) => sum + (o.total_amount || 0), 0).toFixed(2)} ₺
            </span>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingOffer ? 'Teklifi Düzenle' : 'Yeni Abonelik Teklifi Oluştur'}
              <span className="ml-2 px-2 py-1 rounded text-sm font-semibold bg-purple-100 text-purple-800">
                Abonelik
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Müşteri Seçimi */}
            <div className="relative customer-search-container">
              <Label>Müşteri *</Label>
              <Input
                placeholder="Müşteri ara..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (e.target.value.length > 0) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onFocus={() => {
                  if (customerSearch.length > 0 && !formData.customer_id) {
                    setShowCustomerDropdown(true);
                  }
                }}
              />
              
              {/* Seçili Müşteri */}
              {formData.customer_id && !showCustomerDropdown && (
                <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200 flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {customers.find(c => c.id === formData.customer_id)?.name || 
                     prospects.find(p => p.id === formData.customer_id)?.name}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFormData({ ...formData, customer_id: '' });
                      setCustomerSearch('');
                      setShowCustomerDropdown(true);
                    }}
                  >
                    Değiştir
                  </Button>
                </div>
              )}
              
              {/* Dropdown */}
              {showCustomerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {(() => {
                    const query = customerSearch.toLowerCase();
                    const allEntities = [...customers, ...prospects];
                    const startsWithResults = allEntities.filter(entity => 
                      entity.name.toLowerCase().startsWith(query) ||
                      entity.phone?.startsWith(customerSearch)
                    );
                    const wordStartResults = allEntities.filter(entity => 
                      !entity.name.toLowerCase().startsWith(query) &&
                      entity.name.toLowerCase().split(' ').some(word => word.startsWith(query))
                    );
                    return [...startsWithResults, ...wordStartResults].slice(0, 50);
                  })().map(customer => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          setFormData({ ...formData, customer_id: customer.id });
                          setCustomerSearch(customer.name);
                          setShowCustomerDropdown(false);
                        }}
                      >
                        <div className="font-medium">{customer.name}</div>
                        {customer.email && (
                          <div className="text-xs text-gray-500">{customer.email}</div>
                        )}
                        {customer.phone && (
                          <div className="text-xs text-gray-500">{customer.phone}</div>
                        )}
                      </div>
                    ))}
                  {customerSearch.length > 0 && [...customers, ...prospects].filter(e => 
                    e.name.toLowerCase().includes(customerSearch.toLowerCase())
                  ).length === 0 && (
                    <div className="p-3 text-center text-gray-500">
                      Müşteri bulunamadı
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Teklif Başlığı */}
            <div>
              <Label>Teklif Başlığı</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Abonelik Fiyat Teklifi"
              />
            </div>

            {/* Teklif Süresi */}
            <div>
              <Label>Teklif Süresi *</Label>
              <Select
                value={formData.duration_months?.toString()}
                onValueChange={(value) => setFormData({ ...formData, duration_months: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Süre Seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 Ay</SelectItem>
                  <SelectItem value="24">24 Ay</SelectItem>
                  <SelectItem value="36">36 Ay</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ürünler */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Ürünler *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ürün Ekle
                </Button>
              </div>
              
              {formData.items.map((item, index) => {
                const discountedPrice = calculateDiscountedPrice(item);
                const itemTotal = calculateItemTotal(item);
                const yearMultiplier = parseInt(formData.duration_months) / 12;
                return (
                  <div key={index} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-purple-700">Ürün {index + 1}</span>
                        {item.product_name && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                            İndirimli × Adet × {yearMultiplier} Yıl
                          </span>
                        )}
                      </div>
                      {formData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-12 gap-3">
                      {/* Ürün Seçimi */}
                      <div className="col-span-4">
                        <Label className="text-xs">Ürün</Label>
                        <Select
                          value={item.product_name}
                          onValueChange={(value) => updateItem(index, 'product_name', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Ürün Seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {SUBSCRIPTION_PRODUCTS.map((product) => (
                              <SelectItem key={product} value={product}>
                                {product}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Adet */}
                      <div className="col-span-2">
                        <Label className="text-xs">Adet</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      
                      {/* Yıllık Fiyat */}
                      <div className="col-span-2">
                        <Label className="text-xs">Yıllık Fiyat</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={item.annual_price}
                          onChange={(e) => updateItem(index, 'annual_price', e.target.value)}
                        />
                      </div>
                      
                      {/* İndirim Oranı (%) */}
                      <div className="col-span-2">
                        <Label className="text-xs">İndirim %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="0"
                          value={item.discount_percentage || ''}
                          onChange={(e) => updateItem(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      
                      {/* İndirimli Fiyat (Otomatik Hesaplanan) */}
                      <div className="col-span-2">
                        <Label className="text-xs">İndirimli Fiyat</Label>
                        <div className="h-10 flex items-center px-3 bg-white rounded border text-sm font-semibold text-blue-600">
                          {discountedPrice.toFixed(2)} ₺
                        </div>
                      </div>
                    </div>
                    
                    {/* Satır Toplam */}
                    <div className="mt-3 pt-3 border-t border-purple-200 flex justify-end">
                      <div className="text-right">
                        <span className="text-xs text-gray-500 mr-2">Satır Toplam:</span>
                        <span className="text-lg font-bold text-green-600">{itemTotal.toFixed(2)} ₺</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Toplam Özeti */}
            <div className="p-4 bg-gray-50 rounded-lg border space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Ara Toplam</span>
                <span className="font-semibold">{totals.subtotal.toFixed(2)} ₺</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">KDV (%20)</span>
                <span className="font-semibold text-orange-600">{totals.vatAmount.toFixed(2)} ₺</span>
              </div>
              <div className="border-t pt-2 flex justify-between items-center">
                <span className="font-bold text-lg">Genel Toplam (KDV Dahil)</span>
                <span className="text-2xl font-bold text-green-600">{totals.total.toFixed(2)} ₺</span>
              </div>
            </div>

            {/* Notlar */}
            <div>
              <Label>Notlar / Açıklamalar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Teklif ile ilgili ek bilgiler..."
                rows={3}
                className="bg-white border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowDialog(false);
                setEditingOffer(null);
                resetForm();
              }}>
                İptal
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">
                {editingOffer ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Teklif Önizleme</DialogTitle>
          </DialogHeader>
          
          {previewOffer && (
            <div dangerouslySetInnerHTML={{ __html: generateOfferHTML(previewOffer) }} />
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Kapat
            </Button>
            <Button onClick={() => handleDownloadPDF(previewOffer)} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              İndir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionOffers;
